import { useEffect, useRef, useState } from "react";

function clampPoints(raw: string, max = 1000) {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(max, n);
}

/** LearnPath-style points pill: shows `2 pts`, double-click to type a new value. */
export default function PointsChip({
  value,
  suffix = "pts",
  title,
  disabled,
  max = 1000,
  showSuffix = true,
  onChange,
}: {
  value: number;
  suffix?: string;
  title?: string;
  disabled?: boolean;
  max?: number;
  showSuffix?: boolean;
  onChange?: (next: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);
  const canEdit = Boolean(onChange) && !disabled;
  const kind = suffix === "att" ? "att" : "pts";
  const label = title || (kind === "att" ? "Attempt points" : "Points");

  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editing]);

  const commit = () => {
    const next = clampPoints(draft, max);
    setEditing(false);
    if (next !== value) onChange?.(next);
  };

  if (editing && canEdit) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={0}
        max={max}
        className={`al-cb-pts-chip al-cb-pts-chip--edit al-cb-pts-chip--${kind}`}
        value={draft}
        aria-label={label}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            setDraft(String(value));
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <span
      className={`al-cb-pts-chip al-cb-pts-chip--${kind}${canEdit ? "" : " al-cb-pts-chip--locked"}`}
      title={canEdit ? `${label} · double-click to edit` : label}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        if (!canEdit) return;
        e.preventDefault();
        e.stopPropagation();
        setDraft(String(value));
        setEditing(true);
      }}
    >
      {value}{showSuffix && suffix ? ` ${suffix}` : ""}
    </span>
  );
}
